import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { createAnalytics, createIdentityJwtTokenGetter } from '@3plearning/mx-teacher-analytics';
import { AnalyticsProvider } from '@3plearning/mx-teacher-analytics/react';
import appEvents from './events';
import resolveEnabledGroups from './feature-groups';
import { getTeacherAuthToken } from '../helpers/utils';

const APP_NAME = 'assessments-assign';
const TEACHER_TOTTER_PRODUCT = 'mathletics';

// env.sh writes every value as a string, so booleans arrive as 'true'/'false'.
const isTrue = value => value === true || value === 'true';

// TeacherTotter is only permitted for OneRoster-activated schools and only when
// a sync URL is set (the package throws when enabled without one). Analytics
// must never break the app, so a failed build falls back to teacher-totter off.
const buildAnalyticsClient = (params) => {
  const config = {
    app: APP_NAME,
    environment: params.environment,
    providers: {
      posthog: params.posthogEnabled
        ? { enabled: true, apiKey: params.posthogKey || undefined, host: params.posthogHost || undefined }
        : { enabled: false },
      teacherTotter: {
        enabled: params.teacherTotterEnabled && Boolean(params.teacherTotterSyncUrl),
        syncUrl: params.teacherTotterSyncUrl,
        product: TEACHER_TOTTER_PRODUCT,
        getAuthToken: createIdentityJwtTokenGetter({
          identityGatewayUrl: params.identityGatewayUrl,
          getAppAuthToken: params.getAppAuthToken
        })
      }
    },
    enabledFeatureGroups: resolveEnabledGroups(params.trackingGroups),
    events: appEvents
  };

  try {
    return createAnalytics(config);
  } catch (error) {
    console.error('[analytics] Initialisation failed; continuing with teacher-totter disabled.', error);

    return createAnalytics({
      ...config,
      providers: {
        ...config.providers,
        teacherTotter: { ...config.providers.teacherTotter, enabled: false }
      }
    });
  }
};

const AnalyticsAppProvider = ({ children }) => {
  const env = window._env_ || {};
  const params = queryString.parse(window.location.search);
  const isOneRosterActivatedSchool = params.isOneRosterActivatedSchool === 'true';
  // Read the live auth token through a ref so the JWT getter always sees the current value.
  const authTokenRef = useRef(getTeacherAuthToken());

  authTokenRef.current = getTeacherAuthToken();

  const [client] = useState(() => buildAnalyticsClient({
    environment: env.ENVIRONMENT || 'production',
    posthogEnabled: isTrue(env.POSTHOG_ENABLED),
    posthogKey: env.POSTHOG_KEY,
    posthogHost: env.POSTHOG_HOST,
    trackingGroups: env.POSTHOG_TRACKING_GROUPS,
    teacherTotterEnabled: isTrue(env.TEACHER_TOTTER_ENABLED) && isOneRosterActivatedSchool,
    teacherTotterSyncUrl: env.TEACHER_TOTTER_SYNC_URL,
    identityGatewayUrl: env.IDENTITY_GATEWAY_URL,
    getAppAuthToken: () => authTokenRef.current
  }));

  return <AnalyticsProvider client={client}>{children}</AnalyticsProvider>;
};

AnalyticsAppProvider.propTypes = {
  children: PropTypes.node
};

export default AnalyticsAppProvider;
